import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    OnInit,
    PLATFORM_ID,
    ViewChild,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
// ** rxjs
import { Observable, of, Subject } from 'rxjs';
import { finalize, switchMap, tap } from 'rxjs/operators';
// ** service
import { LayoutService } from '@app/core/services/layout.service';
import { ProductsService } from '@app/core/services/products.service';
import { SuccessService } from '@app/core/services/success.service';
// ** primeng
import { Paginator } from 'primeng/paginator';
// ** translate
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '@app/core/services';

@Component({
    selector: 'app-solution-explore',
    templateUrl: './solution-explore.component.html',
    styleUrls: ['./solution-explore.component.scss'],
})
export class SolutionExploreComponent implements OnInit {
    @ViewChild(Paginator) paginator: Paginator;
    @ViewChild('searchBtn') searchBtn;
    @ViewChild('kw') searchInput: ElementRef;

    isBrowser: boolean;

    typeMap = {
        gov: '國軍單位',
        police: '執法應用',
        judicial: '檢調司法',
        business: '工業商業',
        edu: '教育',
        transport: '運輸物流',
        medical: '能源醫療',
        'info-security': '資安',
        'national-security': '國安',
        'public-security': '治安',
		investForensics: '科技偵查與數位鑑識',
		securityMonitor: '智慧安防與場域監控',
		intelGovern: '情資整合與智慧治理',
		logisticsTransport: '智慧物流與運輸管理',
		coreOpsHR: '核心營運與人事管理',
	};

    // ** 搜尋欄位的值
    keywordRaw = '';
    // ** 推薦關鍵字 tag
	recommendTags = [
		'coreOpsHR', // 核心管理
		'police', // 執法應用
		'prosecutorialAndJudicialAuthorities', // 檢調司法
		'transportationAndLogistics', // 運輸物流
		'ai', // 人工智慧
		'bigData', // 大數據
		'informationSecurity', // 資訊安全
		'internetOfThings', // 物聯網
		'technologicalInvestigation', // 科技偵查
		'dataIntegration', // 數據整合
	];

    // ** 畫面上顯示的關鍵字和實際的關鍵字對應
    private tagSearchMapping = {
        'Prosecutorial & Judicial Authoritie':
            'Prosecutorial and Judicial Authoritie',
        'Transportation & Logistics': 'Transportation and Logistics',
        'Information Security': 'Cybersecurity',
        InfoSec: 'Information Security',
        AI: 'Artificial Intelligence',
    };

    // ** 所有的 成功案咧 清單 (全部)
    successSheet = [];
    // ** 所有的 成功案咧 清單 (by 關鍵字搜尋)
    successByKeyword = [];
    // ** 當前應呈現的 成功案咧 資料
    successByPage = [];
    private destroy$ = new Subject<void>();
    // 儲存當前關鍵字
    private currentParamKeyword: string = '';

    constructor(
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId,
        private route: ActivatedRoute,
        private title: Title,
        private router: Router,
        private productsService: ProductsService,
        private successService: SuccessService,
        private layoutService: LayoutService,
        private cdr: ChangeDetectorRef,
        private translateService: TranslateService,
        private languageService: LanguageService,
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);

        // 監聽語系變化，清空輸入框(考量 user 可自行輸入，輸入內容無法對應預設關鍵字語系轉換)
        this.translateService.onLangChange.subscribe(() => {
            this.clearSearchOnLangChange();
        });
    }

    ngOnInit(): void {
        this.initPage();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    initPage() {
        this.route.params
            .pipe(
                tap(() => this.layoutService.setPageLoading(true)),
                tap(params => {
                    const canonical = this.document.querySelector(
                        'link[rel="canonical"]',
                    ) as HTMLAnchorElement | null;
                    if (canonical)
                        canonical.href = `https://www.kunyoutech.com.tw/solutions/explore`;
                }),
                tap(params => {
                    this.currentParamKeyword = params['keyword'] || '';
                    this.keywordRaw = this.currentParamKeyword
                        ? this.getTranslatedTypeFromParam(
                              this.currentParamKeyword,
                          )
                        : '';
                }),
                tap(() => this.getSuccessSheet()),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    // - 取得所有資料 (進到頁面只呼叫一次)
    getSuccessSheet() {
        of(this.layoutService.setPageLoading(true))
            .pipe(
                switchMap(() => this.getSuccessSheet$()),
                tap(res => (this.successSheet = [...res])),
                tap(res => (this.successByKeyword = [...res])),
                tap(() =>
                    this.keywordRaw
                        ? this.searchBtn.nativeElement.click()
                        : this.getDataByPage({ page: 0 }),
                ),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    /** 按下搜尋 - 取交集  */
    getSuccessByKeyword(rawString, e) {
        of(this.layoutService.setPageLoading(true))
            .pipe(
                tap(() => {
                    const mappedString =
                        this.tagSearchMapping[rawString] || rawString;

                    if (!mappedString.trim()) {
                        this.successByKeyword = [...this.successSheet];
                        return;
                    }

                    this.resetSuccessData(e);
                    this.successSheet.forEach(success => {
                        const metaTags = this.isEnglish()
                            ? success.MetaTagsEN ?? []
                            : success.MetaTags ?? [];

                        const name = this.isEnglish()
                            ? success.NameEN ?? success.Name ?? ''
                            : success.Name ?? '';

                        // 取 交集
                        const isRelated = this.checkRelevance(
                            mappedString,
                            metaTags,
                            name,
                        );

                        if (isRelated) {
                            this.successByKeyword.push(success);
                        }
                    });
                }),
                tap(() => this.getDataByPage({ page: 0 })),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    // - 分頁
    getDataByPage(event) {
        of(this.layoutService.setPageLoading(true))
            .pipe(
                tap(
                    () =>
                        (this.successByPage = this.successByKeyword.slice(
                            event.page * 6,
                            (event.page + 1) * 6,
                        )),
                ),
                tap(() => {
                    if (!this.isBrowser) return;
                    let element = document.getElementById('exploreArea');
                    let targetPosition = element.offsetTop - 65;
                    setTimeout(
                        () =>
                            window.scrollTo({
                                top: targetPosition,
                                behavior: 'smooth',
                            }),
                        300,
                    );
                }),
                finalize(() => this.layoutService.setPageLoading(false)),
            )
            .subscribe();
    }

    // - 前往成功案例內頁
    goToSuccess(NameEN) {
        of(this.layoutService.setPageLoading(true))
            .pipe(
                tap(() =>
                    setTimeout(() =>
                        this.router.navigateByUrl(`success/${NameEN}`),
                    ),
                ),
            )
            .subscribe();
    }

    // - reset 成功案例搜尋資料 to default
    resetSuccessData(e) {
        this.successByKeyword = [];
        this.successByPage = [];
        this.paginator?.changePageToFirst(e);
    }

    /**
     * 查詢關鍵字(英文本身字詞就是以空格間隔，單一字詞重複性高)
     * ex: 搜尋 "National Security"，"National Security"、"National"、"Security" 都會被納入，但不見得有關聯
     */
    checkRelevance(keyword: string, metaTags: string[], name: string): boolean {
        const keywordLower = keyword.toLowerCase();

        // 第一優先：完整字配對，ex：搜尋 "National Security"，直接找包含 "National Security" 的 metaTags
        const exactMatch =
            metaTags.some(tag => tag.toLowerCase().includes(keywordLower)) ||
            name.toLowerCase().includes(keywordLower);

        if (exactMatch) {
            return true;
        }

        // 第二優先：短字詞（<2個字）
        const words = keyword.split(' ').filter(w => w.trim() !== '');
        const isChinese = /[\u4e00-\u9fff]/.test(keyword);

        if (words.length < 2 || isChinese) {
            const keywordRegexes = words.map(w => new RegExp(w, 'i'));

            const tokenMatch = keywordRegexes.every(
                regex =>
                    metaTags.some(tag => regex.test(tag)) || regex.test(name),
            );

            if (tokenMatch) {
                return true;
            }
        }

        /**
         * 第三優先：長英文字詞
         * ex：搜尋 "National Security Intelligence Analysis"，需要至少3個字(75%)相符才會被納入
         * */
        if (!isChinese && words.length > 2) {
            const matchCount = words.filter(
                word =>
                    metaTags.some(tag =>
                        tag.toLowerCase().includes(word.toLowerCase()),
                    ) || name.toLowerCase().includes(word.toLowerCase()),
            ).length;

            // 至少要有 70% 的詞相符
            const matchRatio = matchCount / words.length;
            const isRelevant = matchRatio >= 0.7;

            return isRelevant;
        }

        return false;
    }

    /**
     * 根據參數取翻譯後的(從 typeMap 取並帶入搜尋框的關鍵字)
     */
    getTranslatedTypeFromParam(paramKey: string): string {
        if (!paramKey || !this.typeMap[paramKey]) {
            return '';
        }

        const translationKey = `solutions.explore.search.inputTypesSearch.${paramKey}`;
        const translated = this.translateService.instant(translationKey);

        return translated !== translationKey
            ? translated
            : this.typeMap[paramKey];
    }

    /** 判斷當前語系是否為英文 */
    isEnglish(): boolean {
        return this.languageService.getCurrentLang() === 'en-us';
    }

	/**
	 * 英文名稱轉換為對應的 key
	 * ex：'mobile-public-service-duty-app' -> 'mobilePublicServiceDutyApp'
	 */
    getSolutionsKeyFromNameEN(nameEN: string): string {
        return nameEN
            .replace(/-([a-z])/g, (match, letter) => letter.toUpperCase())
            .replace(/^[a-z]/, letter => letter.toLowerCase());
    }

    // 翻譯後的 tag 文字（用於搜尋）
    getTranslatedTagForSearch(tagKey: string): string {
        return this.translateService.instant(
            `solutions.explore.search.recommendTags.${tagKey}`,
        );
    }

    // 加入 tag 到搜尋框
    addTagToKeyword(tagKey: string): void {
        const translatedTag = this.getTranslatedTagForSearch(tagKey).trim();
        if (!translatedTag) return;

        const currentKeyword = (this.keywordRaw ?? '').trim().replace(/\s+/g, ' ');
        // 避免重複加入同一個 tag
        const normalizedKeyword = ` ${currentKeyword.toLowerCase()} `;
        const normalizedTag = ` ${translatedTag.toLowerCase()} `;
        const isExistingTag = normalizedKeyword.includes(normalizedTag);

        this.keywordRaw = isExistingTag
            ? currentKeyword
            : currentKeyword
              ? `${currentKeyword} ${translatedTag}`
              : translatedTag;
    }

    // 語系切換時清空搜尋框
    clearSearchOnLangChange() {
        this.keywordRaw = '';
        if (this.searchInput?.nativeElement) {
            this.searchInput.nativeElement.value = '';
        }
    }

    // ? API

    getSuccessSheet$(): Observable<any> {
        return this.successService.getSuccessSheet().pipe();
    }
}
